import { useMemo } from 'react';
import { type LatLng } from '~/_shared/types/latLng';
import { useMapBoundaryPolygonFilter } from '~/map/map/boundary/boundaryView/mapBoundaryPolygons/useMapBoundaryPolygonFilter';
import { shapeOutlinesToPath } from '~/map/map/mapObjects/mapShape/mapShapeModel';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import {
  type BoundaryAdvancedEditActiveState, BoundaryDrawMode, type BoundaryPolygonEditActiveState,
} from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.state';
import {
  getMostDetailedBoundaryPolygons, toClosedChain,
} from './boundaryDrawSnapTargets.helpers';

export const useBoundaryDrawSnapTargets = (
  drawState: BoundaryPolygonEditActiveState | BoundaryAdvancedEditActiveState | null,
): ReadonlyArray<ReadonlyArray<LatLng>> => {
  const boundaryItems = useMapBoundariesSelector();
  const boundaryPolygonFilter = useMapBoundaryPolygonFilter();

  return useMemo(() => {
    if (!drawState) {
      return [];
    }

    const sessionChains = drawState.mapShapes.map(
      shape => toClosedChain(shapeOutlinesToPath(shape.outlines))
    );

    const { boundaryGroupId, drawModeData } = drawState;
    const editedBoundaryId = drawModeData.boundaryId ?? null;
    const groupBoundaries = boundaryItems.get(boundaryGroupId);

    if (!groupBoundaries) {
      return sessionChains;
    }

    const boundaryGroupFilter = boundaryPolygonFilter.get(boundaryGroupId);
    const persistedChains: Array<ReadonlyArray<LatLng>> = [];

    // Advanced-edit needs the edited boundary's own shapes as snap targets too.
    const isAdvancedEdit = drawModeData.mode === BoundaryDrawMode.BoundaryAdvancedEdit;

    for (const [boundaryId, boundary] of groupBoundaries) {
      if (boundaryGroupFilter?.(boundary) === false || (boundaryId === editedBoundaryId && !isAdvancedEdit)) {
        continue;
      }

      for (const polygon of getMostDetailedBoundaryPolygons(boundary.paths)) {
        persistedChains.push(toClosedChain(polygon.path));
        for (const hole of polygon.holes) {
          persistedChains.push(toClosedChain(hole));
        }
      }
    }

    return [...persistedChains, ...sessionChains];
  }, [drawState, boundaryItems, boundaryPolygonFilter]);
};
